/**
 * Payment Flow Constants
 * Centralizes all configuration values and static definitions for payment flows.
 *
 * @module paymentConstants
 */

/**
 * Timeouts and Delays (in milliseconds)
 */
export const PAYMENT_TIMEOUT_MS = 180000 // 3 minutes timeout for the entire flow
export const POPUPS_CLOSE_DELAY_MS = 300 // Short delay before closing popups after postMessage
export const REDIRECT_DELAY_MS = 500 // Delay before redirecting after payment completion
export const ERROR_REDIRECT_DELAY_MS = 2000 // Longer delay on error page to let user read the message

/**
 * Window Management Configuration
 */
export const WINDOW_OPEN_TIMEOUT_MS = 5000 // Timeout for window to open
export const WINDOW_POLL_INTERVAL_MS = 500 // Interval to check if window opened
export const WINDOW_CLOSE_CHECK_INTERVAL_MS = 1000 // Interval to check if window was closed by user

/**
 * Window features for payment popups
 */
export const PAYMENT_POPUP_FEATURES =
  'width=600,height=800,scrollbars=yes,resizable=yes'

/**
 * Authorized domains for postMessage communication
 * Security: Only messages from these origins will be accepted
 */
export const AUTHORIZED_DOMAINS = {
  airc: [
    'https://*.airc.n3.caffeina.host',
    'https://*.airc.caffeina.host',
    'https://develop.donation-cro.airc.caffeina.host',
    'https://api.prod.airc.it',
  ],
  cookiebot: [
    'https://consent.cookiebot.com',
    'https://consentcdn.cookiebot.com/',
  ],
  satispay: ['https://staging-online.satispay.com/', 'https://*.satispay.com'],
  paypal: [
    'https://*.paypal.com',
    'https://*.paypalobjects.com',
    'https://*.paypalapi.com',
    'https://www.paypal.com',
    'https://www.sandbox.paypal.com',
    'https://cors.api.sandbox.paypal.com',
  ],
  braintree: [
    'https://*.braintreegateway.com',
    'https://*.braintree-api.com',
    'https://assets.braintreegateway.com',
    'https://api.braintreegateway.com',
    'https://client-analytics.braintreegateway.com',
  ],
  poste: ['https://*.poste.it', 'https://acquistionlinetest.poste.it'],
  browserstack: ['https://*.browserstack.com', 'https://*.bs-local.com'],
  criteo: ['https://dynamic.criteo.com', 'http://dynamic.criteo.com'],
  cloudflare: ['https://static.cloudflareinsights.com'],
}

/**
 * API Endpoints for payment operations
 */
export const PAYMENT_ENDPOINTS = {
  PAY: '/api/payments/pay',
  CONFIRM: '/api/donation/confirm',
  TOKEN: '/api/payments/token',
  OPTIONS: '/api/options/payments',
}

/**
 * Payment authentication and authorization statuses
 */
export const PAYMENT_AUTH_STATUS = {
  APPROVED: 'APPROVED',
  YES: 'YES',
  DECLINED: 'DECLINED',
  PENDING: 'PENDING',
}

/**
 * Error types for structured error handling and categorization
 */
export const PAYMENT_ERROR_TYPES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  GATEWAY_ERROR: 'GATEWAY_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  WINDOW_BLOCKED: 'WINDOW_BLOCKED',
  WINDOW_NOT_OPENED: 'WINDOW_NOT_OPENED',
  WINDOW_CLOSED_PREMATURELY: 'WINDOW_CLOSED_PREMATURELY',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
}

/**
 * User-friendly error messages mapped to error types
 */
export const PAYMENT_ERROR_MESSAGES = {
  [PAYMENT_ERROR_TYPES.NETWORK_ERROR]:
    'Errore di connessione. Verifica la tua connessione internet e riprova.',
  [PAYMENT_ERROR_TYPES.VALIDATION_ERROR]:
    'I dati inseriti non sono validi. Controlla e riprova.',
  [PAYMENT_ERROR_TYPES.GATEWAY_ERROR]:
    'Il servizio di pagamento è temporaneamente non disponibile. Riprova tra qualche minuto.',
  [PAYMENT_ERROR_TYPES.TIMEOUT_ERROR]:
    'Il pagamento ha impiegato troppo tempo. Riprova.',
  [PAYMENT_ERROR_TYPES.WINDOW_BLOCKED]:
    'Il popup di pagamento è stato bloccato. Abilita i popup per questo sito e riprova.',
  [PAYMENT_ERROR_TYPES.WINDOW_NOT_OPENED]:
    'La finestra di pagamento non si è aperta. Verifica che i popup siano abilitati e riprova.',
  [PAYMENT_ERROR_TYPES.WINDOW_CLOSED_PREMATURELY]:
    'La finestra di pagamento è stata chiusa. Clicca su "Ritenta" per procedere con il pagamento.',
  [PAYMENT_ERROR_TYPES.UNKNOWN_ERROR]:
    'Si è verificato un errore durante il pagamento. Riprova.',
}

/**
 * Legacy/Generic fallbacks
 */
export const PAYMENT_ERROR_GENERIC = 'Errore durante il pagamento, ritenta'
export const PAYMENT_ERROR_RECURRENT_PREFIX =
  'Errore durante il pagamento ricorrente: '
